import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";

const fmtTime = (s) => {
  if (!s) return "";
  const d = new Date(s);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const isAudioAtt = (a) => (a.content_type || "").startsWith("audio") || /\.(ogg|oga|opus|mp3|m4a|wav|amr)$/i.test(a.filename || "");

function WaAttachment({ att, emailId, index }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gone, setGone] = useState(false);
  if (!isAudioAtt(att)) {
    return <div className="muted" style={{ fontSize: 12 }}>📎 {att.filename || "allegato"}</div>;
  }
  const listen = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/emails/${emailId}/attachments/${index}?inline=1`, { responseType: "blob" });
      setAudioUrl(URL.createObjectURL(new Blob([r.data], { type: att.content_type || "audio/mpeg" })));
    } catch { setGone(true); }
    setLoading(false);
  };
  return (
    <div data-testid={`wa-voice-${emailId}-${index}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12.5 }}>🎙 Messaggio vocale</span>
        {!audioUrl && !gone && (
          <button className="btn sm" style={{ padding: "1px 8px", fontSize: 11 }} onClick={listen} disabled={loading}
            data-testid={`wa-voice-listen-${emailId}-${index}`}>{loading ? "…" : "▶ Ascolta"}</button>
        )}
      </div>
      {audioUrl && <audio controls autoPlay src={audioUrl} style={{ width: 230, height: 34, marginTop: 4 }} />}
      {gone && <div className="muted" style={{ fontSize: 11.5 }}>⚠️ audio non più disponibile</div>}
      {att.transcription
        ? <div className="wa-transcription" data-testid={`wa-transcription-${emailId}-${index}`}>📝 “{att.transcription}”</div>
        : <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>trascrizione in arrivo (automatica entro 30 min)</div>}
    </div>
  );
}

export default function WhatsAppChat() {
  const [convos, setConvos] = useState([]);
  const [sel, setSel] = useState(null); // {phone, name}
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [summary, setSummary] = useState(null); // {loading, text, error}
  const [draftId, setDraftId] = useState("");
  const [saved, setSaved] = useState("");
  const msgsRef = useRef(null);

  const summarize = async () => {
    setSummary({ loading: true });
    try {
      const r = await api.post(`/whatsapp/conversation/${encodeURIComponent(sel.phone)}/summary`);
      setSummary({ text: r.data.summary.replace(/\*\*/g, "").replace(/^#+\s*/gm, ""), count: r.data.messages });
    } catch (e) { setSummary({ error: e.response?.data?.detail || e.message }); }
  };

  const loadConvos = () => api.get("/whatsapp/conversations", { noCache: true }).then((r) => setConvos(r.data)).catch(() => {});
  const loadChat = (phone) => api.get(`/whatsapp/conversation/${encodeURIComponent(phone)}`, { noCache: true })
    .then((r) => { setItems(r.data); setTimeout(() => { const el = msgsRef.current; if (el) el.scrollTop = el.scrollHeight; }, 100); }).catch(() => {});

  useEffect(() => { loadConvos(); const t = setInterval(loadConvos, 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!sel) return;
    setDraftId(""); setSaved("");
    loadChat(sel.phone);
    api.post(`/whatsapp/conversations/${encodeURIComponent(sel.phone)}/read`).then(loadConvos).catch(() => {});
    const t = setInterval(() => loadChat(sel.phone), 20000);
    return () => clearInterval(t);
  }, [sel?.phone]); // eslint-disable-line

  // arrivo da una bozza WhatsApp con ?phone=…: apro direttamente quella chat
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("phone");
    if (p && !sel && convos.length) setSel(convos.find((c) => c.phone === p) || { phone: p, name: p });
  }, [convos]); // eslint-disable-line

  const send = async () => {
    if (busy || !text.trim() || !sel) return;
    setBusy(true);
    try {
      await api.post("/whatsapp/reply", { to: sel.phone, body: text.trim() });
      setText("");
      loadChat(sel.phone);
      loadConvos();
    } catch (e) { alert("Invio fallito: " + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  // salva la risposta come bozza WhatsApp, citando l'ultimo messaggio ricevuto
  const saveDraft = async () => {
    if (busy || !text.trim() || !sel) return;
    setBusy(true);
    const last = [...items].reverse().find((m) => m.direction !== "out");
    try {
      await api.post("/emails/drafts", {
        id: draftId || undefined, channel: "whatsapp", to: sel.phone, wa_name: sel.name || "",
        body_text: text.trim(), reply_to_id: last?.id || null,
        quoted_text: last?.body || "", quoted_at: last?.at || "",
      }).then((r) => setDraftId(r.data.id));
      setSaved("Bozza salvata: la ritrovi in Da gestire → 📝 Bozze.");
      setTimeout(() => setSaved(""), 6000);
    } catch (e) { alert("Bozza non salvata: " + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  const filtered = convos.filter((c) => !q.trim() || (c.name || "").toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q));

  return (
    <div className="wa-app" data-testid="whatsapp-page">
      <aside className="wa-list">
        <div style={{ padding: "12px 12px 8px" }}>
          <h3 style={{ margin: "0 0 8px" }}>WhatsApp</h3>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Cerca chat…" data-testid="wa-search" style={{ width: "100%" }} />
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {filtered.map((c) => (
            <div key={c.phone} className={`wa-convo ${sel?.phone === c.phone ? "active" : ""}`} onClick={() => setSel(c)} data-testid={`wa-convo-${c.phone}`}>
              <div className="wa-avatar">{(c.name || "?").slice(0, 1).toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                  <strong style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</strong>
                  <span className="muted" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{fmtTime(c.last_at)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                  <span className="muted" style={{ fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {c.last_dir === "out" ? "Tu: " : ""}{c.last_body || "(allegato)"}
                  </span>
                  {c.unread > 0 && <span className="wa-badge" data-testid={`wa-unread-${c.phone}`}>{c.unread}</span>}
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="empty" style={{ margin: 16 }}>Nessuna conversazione.</div>}
        </div>
        <div style={{ padding: 10, borderTop: "1px solid var(--border)" }}>
          <Link className="btn sm" to="/impostazioni" data-testid="wa-settings-link">⚙️ Collegamento WhatsApp</Link>
        </div>
      </aside>

      <section className="wa-chat" data-testid="wa-chat">
        {!sel ? (
          <div className="empty" style={{ margin: "auto" }}>Scegli una conversazione a sinistra.</div>
        ) : (
          <>
            <div className="wa-chat-head">
              <div className="wa-avatar">{(sel.name || "?").slice(0, 1).toUpperCase()}</div>
              <div>
                <strong>{sel.name}</strong>
                <div className="muted" style={{ fontSize: 12 }}>{sel.phone}</div>
              </div>
              {sel.company_id && <Link className="btn sm" style={{ marginLeft: "auto" }} to={`/aziende/${sel.company_id}`}>Scheda cliente ↗</Link>}
              <button className="btn sm" style={{ marginLeft: sel.company_id ? 0 : "auto" }} onClick={summarize} data-testid="wa-summary-btn">✨ Riassunto AI</button>
            </div>
            {summary && (
              <div className="modal-overlay" onClick={() => setSummary(null)} data-testid="wa-summary-modal">
                <div className="modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
                  <button className="modal-close" onClick={() => setSummary(null)}>✕</button>
                  <h3>✨ Riassunto della chat con {sel.name}</h3>
                  {summary.loading && <p className="muted">L'AI sta leggendo la conversazione…</p>}
                  {summary.error && <p style={{ color: "#fb7185", fontSize: 13 }}>❌ {summary.error}</p>}
                  {summary.text && (
                    <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.6, maxHeight: "60vh", overflowY: "auto", userSelect: "text" }} data-testid="wa-summary-text">
                      {summary.text}
                    </div>
                  )}
                  {summary.count > 0 && <p className="muted" style={{ fontSize: 11.5, marginBottom: 0 }}>Basato su {summary.count} messaggi (vocali trascritti inclusi).</p>}
                  <div className="modal-actions">
                    <button className="btn" onClick={() => setSummary(null)}>Chiudi</button>
                  </div>
                </div>
              </div>
            )}
            <div className="wa-msgs" ref={msgsRef}>
              {items.map((m, i) => (
                <div key={i} className={`wa-bubble ${m.direction === "out" ? "out" : "in"}`} data-testid={`wa-msg-${i}`}>
                  {m.body && <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>}
                  {(m.attachments || []).map((a, j) => <WaAttachment key={j} att={a} emailId={m.id} index={j} />)}
                  <div className="wa-bubble-time">{fmtTime(m.at)}</div>
                </div>
              ))}
            </div>
            <div className="wa-input">
              <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Scrivi un messaggio… (Invio per inviare)" data-testid="wa-input" />
              <button className="btn" onClick={saveDraft} disabled={busy || !text.trim()} title="Salva come bozza e inviala più tardi dalle Bozze" data-testid="wa-save-draft">💾</button>
              <button className="btn primary" onClick={send} disabled={busy || !text.trim()} data-testid="wa-send">➤</button>
            </div>
            {saved && <div className="muted" style={{ padding: "0 14px 10px", fontSize: 12.5 }} data-testid="wa-draft-saved">{saved}</div>}
          </>
        )}
      </section>
    </div>
  );
}
